import React, { useState, createContext, useContext, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaDice } from 'react-icons/fa';
import { apiFetch } from '../utils/apiUtils';
import { saveActiveSpices } from '../utils/spicePersistence';

// Initial empty config - will be populated from server
let PROMPT_CATEGORIES = {};

// Export for use in other components
export { PROMPT_CATEGORIES };

// Colors from ImageCard.js
const colors = [
  '#00FF9F', // UI Green
  '#FF1493', // Deep pink
  '#0066FF', // Bright blue
  '#FF6B00', // Orange
  '#FF00FF', // Hot pink
  '#FFB800', // Amber
  '#FF4D00', // Bright orange
  '#FF8A00'  // Golden orange
];

// Function to fetch config from server
export const fetchStyleKitConfig = async () => {
  try {
    const config = await apiFetch('/api/stylekit');
    // Update the exported object
    Object.keys(PROMPT_CATEGORIES).forEach(key => delete PROMPT_CATEGORIES[key]);
    Object.assign(PROMPT_CATEGORIES, config);
    return config;
  } catch (error) {
    console.error('[RETRO-STYLEKIT] ❌ Error fetching config:', error);
    // Return fallback config if server fails
    return {
      basePromptStructure: {
        prefix: "close-up detailed headshot of a",
        suffix: ""
      },
      styles: {
        emoji: "🎨",
        name: "Artistic",
        title: "styles",
        maxSelections: null,
        combineType: "designers",
        items: []
      }
    };
  }
};

// Custom tooltip styles with mobile overflow prevention
const tooltipStyles = `
  .spice-tooltip {
    position: relative;
  }
  
  .spice-tooltip::before {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-family: 'VT323', monospace;
    font-size: 11px;
    white-space: normal;
    word-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
    text-align: center;
    line-height: 1.2;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
    transition-delay: 0.5s;
    z-index: 9999;
    pointer-events: none;
    margin-bottom: 5px;
    width: 160px;
    max-width: 160px;
    min-height: 20px;
    box-sizing: border-box;
    
    /* Mobile-specific styles */
    @media (max-width: 640px) {
      white-space: normal;
      word-wrap: break-word;
      max-width: 150px;
      text-align: center;
      line-height: 1.2;
    }
    
    /* Prevent extending beyond viewport */
    @media (max-width: 768px) {
      left: 0;
      right: 0;
      transform: none;
      margin-left: auto;
      margin-right: auto;
      width: fit-content;
      max-width: calc(100vw - 40px);
    }
  }
  
  .spice-tooltip::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
    transition-delay: 0.5s;
    z-index: 9999;
    pointer-events: none;
    margin-bottom: 1px;
    
    /* Mobile-specific arrow positioning */
    @media (max-width: 768px) {
      left: 50%;
      transform: translateX(-50%);
    }
  }
  
  .spice-tooltip:hover::before,
  .spice-tooltip:hover::after {
    opacity: 1;
    visibility: visible;
  }
  
  .spice-tooltip.selected:hover::before,
  .spice-tooltip.selected:hover::after {
    opacity: 1 !important;
    visibility: visible !important;
  }
  
  .spice-tooltip.selected {
    transition: transform 0.2s ease-in-out, background-color 0.2s ease-in-out, opacity 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    transition-delay: 0.15s;
  }
  
  .spice-tooltip:not(:hover)::before,
  .spice-tooltip:not(:hover)::after {
    transition-delay: 0s;
    opacity: 0;
    visibility: hidden;
  }
`;

// Inject styles into the document (only once)
if (typeof document !== 'undefined' && !document.getElementById('spice-tooltip-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'spice-tooltip-styles';
  styleElement.textContent = tooltipStyles;
  document.head.appendChild(styleElement);
}

// Create a context for sharing expansion state
const SpiceRackContext = createContext();
export { SpiceRackContext };

// Provider component
export const SpiceRackProvider = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRandomizing, setIsRandomizing] = useState(false);

  return (
    <SpiceRackContext.Provider value={{ isExpanded, setIsExpanded, isRandomizing, setIsRandomizing }}>
      {children}
    </SpiceRackContext.Provider>
  );
};

const SpiceRack = ({ activeSpices = [], onSpicesChange, className, isFounder = false }) => {
  const context = useContext(SpiceRackContext);
  const [localExpanded, setLocalExpanded] = useState(false);
  const [localRandomizing, setLocalRandomizing] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Use context if available, otherwise use local state
  const isExpanded = context ? context.isExpanded : localExpanded;
  const setIsExpanded = context ? context.setIsExpanded : setLocalExpanded;
  const isRandomizing = context ? context.isRandomizing : localRandomizing;
  const setIsRandomizing = context ? context.setIsRandomizing : setLocalRandomizing;

  // Load config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsLoading(true);
        await fetchStyleKitConfig();
        setConfigLoaded(true);
      } catch (error) {
        console.error('[RETRO-STYLEKIT] ❌ Failed to load config:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!configLoaded) {
      loadConfig();
    }
  }, [configLoaded]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Handle randomize - optionally select from each category, up to max allowed
  const handleRandomize = async (e) => {
    e.stopPropagation(); // Prevent dropdown from opening
    if (!onSpicesChange) return;
    
    // Start animation sequence
    setIsRandomizing(true);
    
    // Small delay to show the "randomizing" state before selection changes
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Perform the randomization
    const randomSpices = [];
    const availableCategories = Object.entries(PROMPT_CATEGORIES)
      .filter(([categoryKey, category]) => category.items && category.items.length > 0);
    
    // EVERY category gets at least 1 selection (matches auto-randomization logic)
    console.log('[SPICE-RACK] 🎲 Manual randomization: EVERY category gets at least 1 selection');
    
    availableCategories.forEach(([categoryKey, category]) => {
      const maxSelections = category.maxSelections;
      const maxToSelect = Math.min(maxSelections || category.items.length, category.items.length);
      
      let numToSelect;
      
      if (maxSelections === 1) {
        // Categories with maxSelections: 1 must have exactly 1
        numToSelect = 1;
      } else if (maxSelections === 2) {
        // Categories with maxSelections: 2 can have 1 or 2 (favor 1 for cleaner results)
        numToSelect = Math.random() > 0.6 ? 2 : 1;
      } else {
        // Unlimited categories: heavily favor 1-2 items (keep reasonable)
        const rand = Math.random();
        if (rand > 0.8) {
          numToSelect = 2; // 20% chance for 2 items
        } else {
          numToSelect = 1; // 80% chance for 1 item
        }
      }
      
      // Randomly select the items
      const shuffled = [...category.items].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, numToSelect);
      
      randomSpices.push(...selected.map(item => item.id));
      
      console.log(`[SPICE-RACK] 📝 Selected from ${categoryKey} (max: ${maxSelections || 'unlimited'}, selected: ${numToSelect}):`, 
        selected.map(item => item.name || item.emoji));
    });
    
    onSpicesChange(randomSpices);
    
    // Save to localStorage for persistence (this is manual randomization, so mark as user action)
    saveActiveSpices(randomSpices, true);
    
    // Show the randomizing animation for a bit longer after selection
    await new Promise(resolve => setTimeout(resolve, 1200));
    
    // End the animation
    setIsRandomizing(false);
  };

  // Show loading state while config is being fetched
  if (isLoading) {
    return (
      <div className={`relative ${className || ''}`}>
        <button
          type="button"
          disabled
          className="pr-8 pl-3 py-2 text-[18px] text-black/40 transition-colors outline-none appearance-none bg-transparent font-['VT323'] flex items-center gap-2"
        >
          <span>Loading Spices...</span>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <div className="w-3 h-3 border border-black/20 border-t-black/40 rounded-full animate-spin"></div>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className={`relative ${className || ''}`}>
      {isRandomizing ? (
        <div className="pr-8 pl-3 py-2 text-[18px] font-['VT323'] flex items-center gap-2">
          <span 
            className="bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 bg-clip-text text-transparent animate-pulse"
            style={{
              background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4, #45B7D1, #96CEB4, #FFEAA7, #DDA0DD)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundSize: '300% 300%',
              animation: 'gradient-shift 1s ease infinite'
            }}
          >
            Randomizing
          </span>
        </div>
      ) : (
        <div className="flex items-center">
          <button
            type="button"
            onClick={toggleExpanded}
            className="pr-2 pl-3 py-2 text-[18px] text-black/40 hover:text-black/60 transition-colors outline-none appearance-none bg-transparent font-['VT323'] flex items-center gap-2"
          >
            <span>
              {activeSpices.length === 0 ? "0 Spices" : 
                activeSpices.length === 1 ? "1 Spice" : 
                `${activeSpices.length} Spices`}
            </span>
            <svg 
              className={`h-4 w-4 text-black/40 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              strokeWidth="1.5" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleRandomize}
            className="px-3 py-2 ml-2 text-[18px] text-black/40 hover:text-black/60 transition-colors outline-none bg-transparent"
            title="Randomize Blatant Spices"
          >
            <FaDice className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// Separate component for the expansion area
export const SpiceRackExpansion = ({ activeSpices = [], onSpicesChange, className, isFounder = false }) => {
  const context = useContext(SpiceRackContext);
  const isExpanded = context ? context.isExpanded : false;
  const setIsExpanded = context ? context.setIsExpanded : () => {};
  const isRandomizing = context ? context.isRandomizing : false;
  const setIsRandomizing = context ? context.setIsRandomizing : () => {};
  const [configLoaded, setConfigLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsLoading(true);
        await fetchStyleKitConfig();
        setConfigLoaded(true);
      } catch (error) {
        console.error('[RETRO-STYLEKIT] ❌ Failed to load config:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!configLoaded) {
      loadConfig();
    }
  }, [configLoaded]);

  // Generate consistent random properties for each spice
  const getSpiceProperties = (spiceId) => {
    // Use spice ID as seed for consistent randomness
    const seed = spiceId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colorIndex = seed % colors.length;
    const rotation = (seed % 21) - 10; // Random rotation between -10 and +10 degrees
    
    return {
      color: colors[colorIndex],
      rotation: rotation
    };
  };

  // Clean up spice names for minimal display
  const getCleanSpiceName = (item, categoryKey) => {
    let name;
    
    // Get the base name based on category
    if (categoryKey === 'film') {
      name = item.prompt.replace(/^,\s*/, '');
    } else if (categoryKey === 'time') {
      name = item.prompt.replace(/^,\s*/, '').replace(/circa\s*/i, '');
    } else {
      name = item.name;
    }
    
    // Clean up the name for minimal display
    return name
      .replace(/\s*mode\s*/gi, '') // Remove "mode" (case insensitive)
      .replace(/\s*boost\s*/gi, '') // Remove "boost" (case insensitive)
      .replace(/\+/g, '') // Remove "+" symbols
      .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
      .trim(); // Remove leading/trailing whitespace
  };

  // Clean up category names for minimal display
  const getCleanCategoryName = (category, categoryKey) => {
    const name = category.name || categoryKey;
    
    // Clean up the category name for minimal display
    return name
      .replace(/\s*mode\s*/gi, '') // Remove "mode" (case insensitive)
      .replace(/\s*boost\s*/gi, '') // Remove "boost" (case insensitive)
      .replace(/\+/g, '') // Remove "+" symbols
      .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
      .trim(); // Remove leading/trailing whitespace
  };

  const handleToggleSpice = (spiceId) => {
    if (!onSpicesChange) return;
    
    // Find which category this spice belongs to
    const categoryKey = Object.keys(PROMPT_CATEGORIES).find(key => {
      const category = PROMPT_CATEGORIES[key];
      return category.items && category.items.some(item => item.id === spiceId);
    });
    
    const category = PROMPT_CATEGORIES[categoryKey];
    
    if (activeSpices.includes(spiceId)) {
      // Remove the spice
      const newActiveSpices = activeSpices.filter(id => id !== spiceId);
      onSpicesChange(newActiveSpices);
    } else {
      // Add the spice
      let newActiveSpices = [...activeSpices, spiceId];
      
      // If this category has maxSelections, enforce the limit
      if (category.maxSelections === 1) {
        // Remove other spices from this category
        const otherSpicesInCategory = category.items
          .map(item => item.id)
          .filter(id => id !== spiceId);
        
        newActiveSpices = newActiveSpices.filter(id => !otherSpicesInCategory.includes(id));
      }
      
      onSpicesChange(newActiveSpices);
      
      // Save to localStorage for persistence (user manually selected spice)
      saveActiveSpices(newActiveSpices, true);
    }
  };

  // Handle randomize - optionally select from each category, up to max allowed
  const handleRandomize = async () => {
    if (!onSpicesChange) return;
    
    // Start animation sequence
    setIsRandomizing(true);
    
    // Small delay to show the "randomizing" state before selection changes
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Perform the randomization
    const randomSpices = [];
    const availableCategories = Object.entries(PROMPT_CATEGORIES)
      .filter(([categoryKey, category]) => category.items && category.items.length > 0);
    
    // EVERY category gets at least 1 selection (matches auto-randomization logic)
    console.log('[SPICE-RACK] 🎲 Manual randomization: EVERY category gets at least 1 selection');
    
    availableCategories.forEach(([categoryKey, category]) => {
      const maxSelections = category.maxSelections;
      const maxToSelect = Math.min(maxSelections || category.items.length, category.items.length);
      
      let numToSelect;
      
      if (maxSelections === 1) {
        // Categories with maxSelections: 1 must have exactly 1
        numToSelect = 1;
      } else if (maxSelections === 2) {
        // Categories with maxSelections: 2 can have 1 or 2 (favor 1 for cleaner results)
        numToSelect = Math.random() > 0.6 ? 2 : 1;
      } else {
        // Unlimited categories: heavily favor 1-2 items (keep reasonable)
        const rand = Math.random();
        if (rand > 0.8) {
          numToSelect = 2; // 20% chance for 2 items
        } else {
          numToSelect = 1; // 80% chance for 1 item
        }
      }
      
      // Randomly select the items
      const shuffled = [...category.items].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, numToSelect);
      
      randomSpices.push(...selected.map(item => item.id));
      
      console.log(`[SPICE-RACK] 📝 Selected from ${categoryKey} (max: ${maxSelections || 'unlimited'}, selected: ${numToSelect}):`, 
        selected.map(item => item.name || item.emoji));
    });
    
    onSpicesChange(randomSpices);
    
    // Save to localStorage for persistence (this is manual randomization, so mark as user action)
    saveActiveSpices(randomSpices, true);
    
    // Show the randomizing animation for a bit longer after selection
    await new Promise(resolve => setTimeout(resolve, 1200));
    
    // End the animation
    setIsRandomizing(false);
  };



  // Show loading state while config is being fetched
  if (isLoading && isExpanded) {
    return (
      <div className={`w-full ${className || ''}`}>
        <div className="w-full px-2 py-2">
          <div className="max-w-6xl mx-auto rounded-xl p-4" style={{ backgroundColor: 'rgba(195, 194, 192, 0.25)' }}>
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border border-black/20 border-t-black/60 rounded-full animate-spin"></div>
                <span className="text-black/60 font-['VT323'] text-lg">Loading Spices...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    isExpanded && (
      <div className={`w-full ${className || ''}`}>
        <div className="w-full px-2 py-2">
          <div className="max-w-6xl mx-auto rounded-xl p-4" style={{ backgroundColor: 'rgba(195, 194, 192, 0.25)' }}>
            
            {/* Top Action Buttons */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleRandomize}
                  className="px-4 py-2 bg-black/[0.03] rounded-lg font-['VT323'] text-[16px] text-black/60 transition-all duration-300 relative overflow-hidden hover-rainbow"
                  style={{
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = `linear-gradient(45deg, ${colors[0]}, ${colors[1]}, ${colors[2]}, ${colors[3]}, ${colors[4]}, ${colors[5]}, ${colors[6]}, ${colors[7]})`;
                    e.target.style.backgroundSize = '200% 200%';
                    e.target.style.animation = 'gradient-shift 1s ease infinite';
                    e.target.style.color = 'white';
                    e.target.style.textShadow = '0 1px 2px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(0, 0, 0, 0.03)';
                    e.target.style.animation = 'none';
                    e.target.style.color = 'rgba(0, 0, 0, 0.6)';
                    e.target.style.textShadow = 'none';
                  }}
                >
                  Randomize
                </button>
                {activeSpices.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onSpicesChange && onSpicesChange([])}
                    className="px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[16px] text-black/60 hover:text-black/80 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[16px] text-black/60 hover:text-black/80 transition-colors"
              >
                Hide
              </button>
            </div>

            {/* Spice Categories - Two panel layout on wide screens */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {Object.entries(PROMPT_CATEGORIES)
                .filter(([categoryKey, category]) => category.items && category.items.length > 0)
                .map(([categoryKey, category]) => {
                const activeSpicesInCategory = activeSpices.filter(spiceId => 
                  category.items.some(item => item.id === spiceId)
                );
                
                return (
                  <div key={`category-${categoryKey}`} className="space-y-3">
                    {/* Category Header */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-['VT323'] font-medium text-black/80 uppercase tracking-wider">
                        {category.emoji} {getCleanCategoryName(category, categoryKey)}
                      </span>
                      
                      {/* Show count of active spices in this category */}
                      {activeSpicesInCategory.length > 0 && (
                        <span className="text-xs font-['VT323'] px-2 py-0.5 rounded-full text-black/60 bg-black/5">
                          {activeSpicesInCategory.length}
                        </span>
                      )}
                      
                      {/* Selection constraint info */}
                      {category.maxSelections === 1 && (
                        <span className="text-xs text-black/50 font-['VT323']">
                          (select one)
                        </span>
                      )}
                    </div>
                    
                    {/* Category Items */}
                    <div className="flex flex-wrap gap-1.5">
                      {category.items.map((item) => {
                        const isSelected = activeSpices.includes(item.id);
                        const spiceProps = getSpiceProperties(item.id);
                        
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => handleToggleSpice(item.id)}
                            data-tooltip={item.description}
                            className={`rounded-full px-2 py-1 flex items-center gap-1 border inline-flex spice-tooltip ${
                              isSelected 
                                ? 'border-transparent selected' 
                                : 'border-gray-200 hover:border-gray-400 transition-all duration-200'
                            }`}
                            style={{
                              backgroundColor: isSelected ? spiceProps.color : '#F5F4F2',
                              transform: isSelected ? `rotate(${spiceProps.rotation * 0.5}deg)` : 'rotate(0deg)',
                              transition: 'transform 0.15s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                            }}
                          >
                            <span 
                              className="text-xs transition-all duration-200"
                              style={{
                                filter: isSelected ? 'grayscale(0%) saturate(130%)' : 'grayscale(30%)',
                                color: isSelected 
                                  ? (spiceProps.color === '#00FF9F' ? 'rgba(0, 0, 0, 0.9)' : 'white')
                                  : 'rgba(0, 0, 0, 0.7)'
                              }}
                            >
                              {item.emoji}
                            </span>
                            <span
                              className="font-['VT323'] text-xs whitespace-nowrap"
                              style={{
                                color: isSelected 
                                  ? (spiceProps.color === '#00FF9F' ? 'rgba(0, 0, 0, 0.9)' : 'white')
                                  : 'rgba(0, 0, 0, 0.7)'
                              }}
                            >
                              {getCleanSpiceName(item, categoryKey)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* CSS Animation for gradient */}
        <style>{`
          @keyframes gradient-shift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
      </div>
    )
  );
};

// Helper function to build the final prompt with spices
export const buildPromptWithSpices = (basePrompt, activeSpices) => {
  if (!basePrompt.trim()) return '';
  
  // Group active spices by category
  const spicesByCategory = {};
  
  activeSpices.forEach(spiceId => {
    const categoryKey = Object.keys(PROMPT_CATEGORIES).find(key =>
      PROMPT_CATEGORIES[key].items && PROMPT_CATEGORIES[key].items.some(item => item.id === spiceId)
    );
    
    if (categoryKey) {
      const category = PROMPT_CATEGORIES[categoryKey];
      const item = category.items.find(item => item.id === spiceId);
      
      if (!spicesByCategory[categoryKey]) {
        spicesByCategory[categoryKey] = {
          category,
          items: []
        };
      }
      
      spicesByCategory[categoryKey].items.push(item);
    }
  });
  
  // Build the structured prompt using the template approach
  const baseStructure = PROMPT_CATEGORIES.basePromptStructure;
  let finalPrompt = '';
  
  // Start with the prefix
  if (baseStructure && baseStructure.prefix) {
    finalPrompt += baseStructure.prefix + ' ';
  }
  
  // Add descriptive adjectives before the THING
  if (spicesByCategory.descriptors) {
    const adjectives = spicesByCategory.descriptors.items.map(item => item.prompt);
    if (adjectives.length > 0) {
      finalPrompt += adjectives.join(', ') + ' ';
    }
  }
  
  // Add culture vibes before the THING (base prompt)
  if (spicesByCategory.vibes) {
    spicesByCategory.vibes.items.forEach(item => {
      // Remove leading comma and space, then add as adjective
      const cleanVibe = item.prompt.replace(/^,\s*/, '');
      finalPrompt += cleanVibe + ' ';
    });
  }
  
  // Add the THING (base prompt)
  finalPrompt += basePrompt;
  
  // Add designers
  if (spicesByCategory.styles) {
    const names = spicesByCategory.styles.items.map(item => item.prompt);
    if (names.length === 1) {
      finalPrompt += ` designed by ${names[0]}`;
    } else if (names.length === 2) {
      finalPrompt += ` designed by ${names[0]} and ${names[1]}`;
    } else if (names.length > 2) {
      const allButLast = names.slice(0, -1).join(', ');
      const last = names[names.length - 1];
      finalPrompt += ` designed by ${allButLast} and ${last}`;
    }
  }
  
  // Add photographers
  if (spicesByCategory.lens) {
    const names = spicesByCategory.lens.items.map(item => item.prompt);
    if (names.length === 1) {
      finalPrompt += `. photographed by ${names[0]}`;
    } else if (names.length === 2) {
      finalPrompt += `. photographed by ${names[0]} and ${names[1]}`;
    } else if (names.length > 2) {
      const allButLast = names.slice(0, -1).join(', ');
      const last = names[names.length - 1];
      finalPrompt += `. photographed by ${allButLast} and ${last}`;
    }
  }
  
  // Add time periods
  if (spicesByCategory.time) {
    const timeItems = spicesByCategory.time.items;
    if (timeItems.length > 0) {
      // Extract the year from the prompt (e.g., ", circa 1970" -> "1970")
      const timePrompt = timeItems[0].prompt;
      const yearMatch = timePrompt.match(/\d{4}/);
      if (yearMatch) {
        finalPrompt += `. Circa ${yearMatch[0]}`;
      }
    }
  }
  
  // Add film formats
  if (spicesByCategory.film) {
    const filmItems = spicesByCategory.film.items;
    if (filmItems.length > 0) {
      // Extract the format from the prompt (e.g., ", 35mm" -> "35mm")
      const filmPrompt = filmItems[0].prompt;
      const cleanFormat = filmPrompt.replace(/^,\s*/, ''); // Remove leading comma and space
      finalPrompt += `. ${cleanFormat}`;
    }
  }
  
  // Add remaining vibes/styles that use "separate" combineType (excluding vibes which are now handled above)
  Object.entries(spicesByCategory).forEach(([categoryKey, { category, items }]) => {
    if (category.combineType === 'separate' && categoryKey !== 'time' && categoryKey !== 'film' && categoryKey !== 'vibes') {
      items.forEach(item => {
        finalPrompt += item.prompt;
      });
    }
  });
  
  return finalPrompt;
};

// Helper function to extract just the base prompt (hide Style Kit additions)
export const getBasePromptOnly = (fullPrompt) => {
  if (!fullPrompt) return '';
  
  let basePrompt = fullPrompt;
  
  // Step 1: Remove the prefix if it exists
  const baseStructure = PROMPT_CATEGORIES.basePromptStructure;
  if (baseStructure && baseStructure.prefix) {
    const prefixRegex = new RegExp(`^${baseStructure.prefix}\\s+`, 'i');
    basePrompt = basePrompt.replace(prefixRegex, '');
  }
  
  // Step 2: Remove all Style Kit elements in the correct order
  // First remove photographers (they come after periods)
  if (PROMPT_CATEGORIES.lens) {
    PROMPT_CATEGORIES.lens.items.forEach(item => {
      // Remove both ". photographed by [name]" and "photographed by [name]" patterns
      const photographerRegex1 = new RegExp(`\\.\\s*photographed by [^.]*${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^.]*`, 'gi');
      const photographerRegex2 = new RegExp(`photographed by [^.]*${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^.]*`, 'gi');
      basePrompt = basePrompt.replace(photographerRegex1, '');
      basePrompt = basePrompt.replace(photographerRegex2, '');
    });
  }
  
  // Remove designers
  if (PROMPT_CATEGORIES.styles) {
    PROMPT_CATEGORIES.styles.items.forEach(item => {
      const designerRegex = new RegExp(`\\s+designed by [^.]*${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^.]*`, 'gi');
      basePrompt = basePrompt.replace(designerRegex, '');
    });
  }
  
  // Remove time periods (handle both ", circa 1970" and "Circa 1970" formats)
  if (PROMPT_CATEGORIES.time) {
    PROMPT_CATEGORIES.time.items.forEach(item => {
      // Extract the year from the config (e.g., ", circa 1970" -> "1970")
      const yearMatch = item.prompt.match(/\d{4}/);
      if (yearMatch) {
        const year = yearMatch[0];
        // Remove both "Circa YYYY" and ", circa YYYY" patterns
        const timeRegex1 = new RegExp(`\\s*\\.?\\s*Circa\\s+${year}`, 'gi');
        const timeRegex2 = new RegExp(`${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi');
        basePrompt = basePrompt.replace(timeRegex1, '');
        basePrompt = basePrompt.replace(timeRegex2, '');
      }
    });
  }
  
  // Remove film formats (handle both ", 35mm" and "35mm" formats)
  if (PROMPT_CATEGORIES.film) {
    PROMPT_CATEGORIES.film.items.forEach(item => {
      // Extract the format from the config (e.g., ", 35mm" -> "35mm")
      const format = item.prompt.replace(/^,\s*/, '');
      // Remove both "35mm" and ", 35mm" patterns
      const filmRegex1 = new RegExp(`\\s*\\.?\\s*${format.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi');
      const filmRegex2 = new RegExp(`${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi');
      basePrompt = basePrompt.replace(filmRegex1, '');
      basePrompt = basePrompt.replace(filmRegex2, '');
    });
  }
  
  // Remove vibes
  if (PROMPT_CATEGORIES.vibes) {
    PROMPT_CATEGORIES.vibes.items.forEach(item => {
      const vibeRegex = new RegExp(`${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi');
      basePrompt = basePrompt.replace(vibeRegex, '');
    });
  }
  
  // Remove adjectives (descriptors) - be more careful here
  if (PROMPT_CATEGORIES.descriptors) {
    PROMPT_CATEGORIES.descriptors.items.forEach(item => {
      // Remove adjectives that appear at the beginning or after commas
      const adjectiveRegex = new RegExp(`(^|,\\s*)${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(,\\s*|\\s+)`, 'gi');
      basePrompt = basePrompt.replace(adjectiveRegex, '$1');
    });
  }
  
  // Step 3: Clean up formatting issues
  basePrompt = basePrompt
    .replace(/\.\s*\.\s*/g, '. ') // Fix double periods
    .replace(/\s*,\s*,\s*/g, ', ') // Fix double commas
    .replace(/^,\s*/, '') // Remove leading comma
    .replace(/,\s*$/, '') // Remove trailing comma
    .replace(/\.\s*$/, '') // Remove trailing period
    .replace(/\s+/g, ' ') // Fix multiple spaces
    .replace(/\s*\.\s*/g, '. ') // Fix spacing around periods
    .trim();
  
  return basePrompt;
};

// Helper function to extract which emojis were used from a full prompt
export const getStyleEmojisFromPrompt = (fullPrompt) => {
  if (!fullPrompt) return [];
  
  const usedEmojis = [];
  
  // Check each category and item for matches
  Object.entries(PROMPT_CATEGORIES).forEach(([categoryKey, category]) => {
    if (!category.items) return; // Skip basePromptStructure
    
    category.items.forEach(item => {
      // Check for different prompt patterns based on category type
      if (category.combineType === 'designers') {
        // Check for "designed by [name]" or "in the style of [name]"
        const designerRegex = new RegExp(`designed by [^.]*${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^.]*`, 'i');
        const styleRegex = new RegExp(`in the style of [^.]*${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^.]*`, 'i');
        if (designerRegex.test(fullPrompt) || styleRegex.test(fullPrompt)) {
          usedEmojis.push(item.emoji);
        }
      } else if (category.combineType === 'photographers') {
        // Check for "photographed by [name]"
        const photographerRegex = new RegExp(`photographed by [^.]*${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^.]*`, 'i');
        if (photographerRegex.test(fullPrompt)) {
          usedEmojis.push(item.emoji);
        }
      } else if (category.combineType === 'adjectives') {
        // Check for adjectives in the descriptors section
        const baseStructure = PROMPT_CATEGORIES.basePromptStructure;
        if (baseStructure && baseStructure.prefix) {
          // Look for the adjective after the prefix
          const prefixRegex = new RegExp(`${baseStructure.prefix}\\s+[^.]*(${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})[^.]*`, 'i');
          if (prefixRegex.test(fullPrompt)) {
            usedEmojis.push(item.emoji);
          }
        }
      } else {
        // For other categories (time, film, etc.), check for exact prompt match
        if (fullPrompt.includes(item.prompt)) {
          usedEmojis.push(item.emoji);
        }
      }
    });
  });
  
  return usedEmojis;
};

// Legacy ID mapping for backward compatibility
const LEGACY_ID_MAPPING = {
  // styles category
  "henson": "styles-henson",
  "mollo": "styles-mollo", 
  "basquiat": "styles-basquiat",
  
  // descriptors category
  "unbelievable": "descriptors-unbelievable",
  "stoned": "descriptors-stoned",
  "awkward": "descriptors-awkward",
  "sci-fi": "descriptors-sci-fi",
  "dreamy": "descriptors-dreamy",
  "cyberpunk": "descriptors-cyberpunk",
  "ethereal": "descriptors-ethereal",
  "mysterious": "descriptors-mysterious",
  "vintage": "descriptors-vintage",
  
  // vibes category
  "hip-hop": "vibes-hip-hop",
  "punk": "vibes-punk",
  "gothic": "vibes-gothic",
  "bohemian": "vibes-bohemian",
  "surreal": "vibes-surreal",
  "industrial": "vibes-industrial",
  
  // lens category
  "bourdin": "lens-bourdin",
  "newton": "lens-newton",
  "avedon": "lens-avedon",
  
  // time category
  "1970s": "time-1970s",
  "1960s": "time-1960s", 
  "1980s": "time-1980s",
  "1990s": "time-1990s",
  
  // film category
  "35mm": "film-35mm",
  "polaroid": "film-polaroid",
  "medium-format": "film-medium-format"
};

// Helper function to migrate legacy spice IDs to new format
const migrateLegacySpiceIds = (spiceIds) => {
  if (!spiceIds || !Array.isArray(spiceIds)) return spiceIds;
  
  return spiceIds.map(id => LEGACY_ID_MAPPING[id] || id);
};

// Helper function to get emojis directly from active spice IDs
export const getEmojisFromActiveSpices = (activeSpices) => {
  if (!activeSpices || activeSpices.length === 0) {
    return [];
  }
  
  // Migrate legacy IDs to new format
  const migratedSpices = migrateLegacySpiceIds(activeSpices);
  
  const emojis = [];
  
  // Look through all categories to find items matching the active spice IDs
  Object.entries(PROMPT_CATEGORIES).forEach(([categoryKey, category]) => {
    if (!category.items) {
      return; // Skip basePromptStructure
    }
    
    category.items.forEach(item => {
      if (migratedSpices.includes(item.id)) {
        emojis.push(item.emoji);
      }
    });
  });
  
  return emojis;
};

// Helper function to extract which spice IDs were used from a full prompt
export const getSpiceIdsFromPrompt = (fullPrompt) => {
  if (!fullPrompt) return [];
  
  const usedSpiceIds = [];
  
  // Check each category and item for matches
  Object.entries(PROMPT_CATEGORIES).forEach(([categoryKey, category]) => {
    if (!category.items) return; // Skip basePromptStructure
    
    category.items.forEach(item => {
      let found = false;
      
      // Check for different prompt patterns based on category type
      if (category.combineType === 'designers') {
        // Check for "designed by [name]" or "in the style of [name]"
        const designerRegex = new RegExp(`designed by [^.]*${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^.]*`, 'i');
        const styleRegex = new RegExp(`in the style of [^.]*${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^.]*`, 'i');
        if (designerRegex.test(fullPrompt) || styleRegex.test(fullPrompt)) {
          found = true;
          usedSpiceIds.push(item.id);
        }
      } else if (category.combineType === 'photographers') {
        // Check for "photographed by [name]" - fixed to handle multiple photographers properly
        const escapedPrompt = item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const photographerRegex = new RegExp(`photographed by[^.]*\\b${escapedPrompt}\\b`, 'i');
        if (photographerRegex.test(fullPrompt)) {
          found = true;
          usedSpiceIds.push(item.id);
        }
      } else if (category.combineType === 'adjectives') {
        // Check for adjectives in the descriptors section
        const baseStructure = PROMPT_CATEGORIES.basePromptStructure;
        if (baseStructure && baseStructure.prefix) {
          // Look for the adjective after the prefix
          const prefixRegex = new RegExp(`${baseStructure.prefix}\\s+[^.]*(${item.prompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})[^.]*`, 'i');
          if (prefixRegex.test(fullPrompt)) {
            found = true;
            usedSpiceIds.push(item.id);
          }
        }
      } else if (category.combineType === 'separate') {
        // Handle film and time categories specially
        if (categoryKey === 'film') {
          // Film formats: config has ", 35mm" but prompt contains ". 35mm" or ".35mm"
          const cleanFormat = item.prompt.replace(/^,\s*/, ''); // Remove leading comma and space
          const filmRegex = new RegExp(`\\.\\s*${cleanFormat.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
          if (filmRegex.test(fullPrompt)) {
            found = true;
            usedSpiceIds.push(item.id);
          }
        } else if (categoryKey === 'time') {
          // Time periods: config has ", circa 1970" but prompt contains ". Circa 1970"
          const yearMatch = item.prompt.match(/\d{4}/);
          if (yearMatch) {
            const year = yearMatch[0];
            const timeRegex = new RegExp(`\\.\\s*Circa\\s+${year}`, 'i');
            if (timeRegex.test(fullPrompt)) {
              found = true;
              usedSpiceIds.push(item.id);
            }
          }
        } else {
          // For other 'separate' categories (vibes), check for exact prompt match
          if (fullPrompt.includes(item.prompt)) {
            found = true;
            usedSpiceIds.push(item.id);
          }
        }
      } else {
        // For other categories, check for exact prompt match
        if (fullPrompt.includes(item.prompt)) {
          found = true;
          usedSpiceIds.push(item.id);
        }
      }
    });
  });
  
  return usedSpiceIds;
};

// Remove the ActiveSpicePills component since we're not using it anymore
export const ActiveSpicePills = () => null;

export default SpiceRack; 